import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, Filter, Download, MoreVertical, Eye, EyeOff, Trash2, 
  ChevronLeft, ChevronRight, Loader2, Package, User, Calendar,
  DollarSign, Clock, Tag, CheckCircle, XCircle, RefreshCw, X
} from 'lucide-react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Auto-suggest input component for provider filtering
function ProviderAutoSuggest({ providers, value, onChange, onSelect }) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Update input value when external value changes (for clear functionality)
  useEffect(() => {
    if (value === 'all') {
      setInputValue('');
    } else {
      const provider = providers.find(p => p.id === value);
      if (provider) {
        setInputValue(provider.name);
      }
    }
  }, [value, providers]);

  // Filter providers based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredProviders(providers.slice(0, 10)); // Show first 10 when empty
    } else {
      const filtered = providers.filter(p => 
        p.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        (p.email && p.email.toLowerCase().includes(inputValue.toLowerCase()))
      ).slice(0, 10);
      setFilteredProviders(filtered);
    }
  }, [inputValue, providers]);

  // Handle click outside to close suggestions
  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setShowSuggestions(true);
    
    // If cleared, reset filter
    if (!val.trim()) {
      onSelect('all');
    }
  };

  const handleSelectProvider = (provider) => {
    setInputValue(provider.name);
    setShowSuggestions(false);
    onSelect(provider.id);
  };

  const handleClear = () => {
    setInputValue('');
    onSelect('all');
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <div className="relative">
        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          ref={inputRef}
          placeholder="Search provider..."
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          className="pl-10 pr-8"
          data-testid="provider-filter-input"
        />
        {inputValue && (
          <button 
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {showSuggestions && filteredProviders.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {inputValue === '' && (
            <div 
              className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-slate-600 border-b"
              onClick={() => { setInputValue(''); setShowSuggestions(false); onSelect('all'); }}
            >
              All Providers
            </div>
          )}
          {filteredProviders.map(provider => (
            <div 
              key={provider.id}
              className="px-3 py-2 hover:bg-amber-50 cursor-pointer flex items-center gap-2"
              onClick={() => handleSelectProvider(provider)}
            >
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 truncate">{provider.name}</p>
                {provider.email && (
                  <p className="text-xs text-slate-500 truncate">{provider.email}</p>
                )}
              </div>
            </div>
          ))}
          {filteredProviders.length === 0 && inputValue && (
            <div className="px-3 py-4 text-center text-slate-500 text-sm">
              No providers found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState({});
  const [providersList, setProvidersList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalServices, setTotalServices] = useState(0);
  
  // Selection
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, service: null });
  const [viewDialog, setViewDialog] = useState({ open: false, service: null });
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    categories: 0
  });

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all provider services
      const res = await axios.get(`${API}/api/admin/provider-services`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          page_size: pageSize,
          search: searchQuery || undefined,
          category_id: categoryFilter !== 'all' ? categoryFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          provider_id: providerFilter !== 'all' ? providerFilter : undefined
        }
      });
      
      setServices(res.data.services || []);
      setTotalServices(res.data.total || 0);
      setStats(res.data.stats || { total: 0, active: 0, inactive: 0, categories: 0 });
      setProviders(res.data.providers || {});
      
    } catch (err) {
      console.error('Failed to fetch services:', err);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, categoryFilter, statusFilter, providerFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchProvidersList = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/admin/providers-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProvidersList(res.data || []);
    } catch (err) {
      console.error('Failed to fetch providers list:', err);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    fetchCategories();
    fetchProvidersList();
  }, [fetchCategories, fetchProvidersList]);

  const handleToggleStatus = async (service) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = !service.is_active;
      
      await axios.put(`${API}/api/admin/provider-services/${service.id}`, 
        { is_active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Service ${newStatus ? 'enabled' : 'disabled'}`);
      fetchServices();
    } catch (err) {
      toast.error('Failed to update service status');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.service) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/provider-services/${deleteDialog.service.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Service deleted successfully');
      setDeleteDialog({ open: false, service: null });
      fetchServices();
    } catch (err) {
      toast.error('Failed to delete service');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedServices.length === 0) {
      toast.error('No services selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      if (action === 'enable' || action === 'disable') {
        await axios.post(`${API}/api/admin/provider-services/bulk-update`, 
          { service_ids: selectedServices, is_active: action === 'enable' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${selectedServices.length} services ${action}d`);
      } else if (action === 'delete') {
        await axios.post(`${API}/api/admin/provider-services/bulk-delete`,
          { service_ids: selectedServices },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${selectedServices.length} services deleted`);
      }
      
      setSelectedServices([]);
      setSelectAll(false);
      fetchServices();
    } catch (err) {
      toast.error(`Failed to ${action} services`);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        format,
        search: searchQuery || '',
        category_id: categoryFilter !== 'all' ? categoryFilter : '',
        status: statusFilter !== 'all' ? statusFilter : '',
        provider_id: providerFilter !== 'all' ? providerFilter : ''
      });
      
      const res = await axios.get(`${API}/api/admin/provider-services/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `services-export-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Services exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to export services');
    } finally {
      setExporting(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedServices(services.map(s => s.id));
    } else {
      setSelectedServices([]);
    }
  };

  const handleSelectService = (serviceId, checked) => {
    if (checked) {
      setSelectedServices([...selectedServices, serviceId]);
    } else {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
      setSelectAll(false);
    }
  };

  const totalPages = Math.ceil(totalServices / pageSize);

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  const getProviderName = (providerId) => {
    return providers[providerId]?.name || 'Unknown Provider';
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Quote';
    return `₱${price.toLocaleString()}`;
  };

  const formatDuration = (hours) => {
    if (!hours) return '-';
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    return `${hours} hr${hours !== 1 ? 's' : ''}`;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile Nav */}
      <AdminMobileNav />
      
      <div className="flex">
        {/* Sidebar - hidden on mobile */}
        <AdminSidebar />
        
        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 md:ml-64 mt-16 md:mt-0">
          <div className="max-w-7xl mx-auto space-y-6" data-testid="admin-services-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Services Management</h1>
                <p className="text-slate-500 mt-1">Manage all provider services across the platform</p>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => fetchServices()} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={exporting}>
                      {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => handleExport('csv')}>
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('pdf')}>
                      Export as PDF
                    </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Services</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.inactive}</p>
                <p className="text-sm text-slate-500">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Tag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.categories}</p>
                <p className="text-sm text-slate-500">Categories Used</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2 lg:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="search-services-input"
              />
            </div>
            
            {/* Provider Filter - Auto-suggest */}
            <ProviderAutoSuggest 
              providers={providersList}
              value={providerFilter}
              onSelect={(v) => { setProviderFilter(v); setCurrentPage(1); }}
            />
            
            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Page Size */}
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Bulk Actions */}
          {selectedServices.length > 0 && (
            <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-blue-700">
                {selectedServices.length} selected
              </span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('enable')}>
                <Eye className="w-4 h-4 mr-1" /> Enable
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('disable')}>
                <EyeOff className="w-4 h-4 mr-1" /> Disable
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')}>
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="services-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-4 text-left">
                    <Checkbox 
                      checked={selectAll} 
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Service</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Provider</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Category</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Price</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Duration</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Status</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Created</th>
                  <th className="p-4 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-2" />
                      <p className="text-slate-500">Loading services...</p>
                    </td>
                  </tr>
                ) : services.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center">
                      <Package className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No services found</p>
                      <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  services.map(service => (
                    <tr key={service.id} className="border-b hover:bg-slate-50" data-testid={`service-row-${service.id}`}>
                      <td className="p-4">
                        <Checkbox 
                          checked={selectedServices.includes(service.id)}
                          onCheckedChange={(checked) => handleSelectService(service.id, checked)}
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {service.photo_url ? (
                            <img src={service.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Package className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{service.name}</p>
                            {service.description && (
                              <p className="text-sm text-slate-500 line-clamp-1 max-w-[200px]">{service.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-amber-600" />
                          </div>
                          <span className="text-sm text-slate-700">{getProviderName(service.provider_id)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          {getCategoryName(service.category_id)}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-slate-900">{formatPrice(service.price)}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-600">{formatDuration(service.duration_hours)}</span>
                      </td>
                      <td className="p-4">
                        <Badge variant={service.is_active ? 'default' : 'secondary'} 
                          className={service.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          {service.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-500">
                          {service.created_at ? new Date(service.created_at).toLocaleDateString() : '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setViewDialog({ open: true, service })}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(service)}>
                              {service.is_active ? (
                                <><EyeOff className="w-4 h-4 mr-2" /> Disable</>
                              ) : (
                                <><Eye className="w-4 h-4 mr-2" /> Enable</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setDeleteDialog({ open: true, service })}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalServices > 0 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalServices)} of {totalServices} services
              </p>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ open, service: viewDialog.service })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Service Details</DialogTitle>
          </DialogHeader>
          {viewDialog.service && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                {viewDialog.service.photo_url ? (
                  <img src={viewDialog.service.photo_url} alt="" className="w-24 h-24 rounded-xl object-cover" />
                ) : (
                  <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Package className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900">{viewDialog.service.name}</h3>
                  <p className="text-slate-500 mt-1">{viewDialog.service.description || 'No description'}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <Badge variant={viewDialog.service.is_active ? 'default' : 'secondary'}>
                      {viewDialog.service.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline">{getCategoryName(viewDialog.service.category_id)}</Badge>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-sm text-slate-500">Provider</p>
                  <p className="font-medium">{getProviderName(viewDialog.service.provider_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Price</p>
                  <p className="font-medium">{formatPrice(viewDialog.service.price)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Duration</p>
                  <p className="font-medium">{formatDuration(viewDialog.service.duration_hours)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Created</p>
                  <p className="font-medium">{viewDialog.service.created_at ? new Date(viewDialog.service.created_at).toLocaleString() : '-'}</p>
                </div>
              </div>
              
              {viewDialog.service.has_warranty && (
                <div className="p-4 bg-green-50 rounded-xl">
                  <h4 className="font-medium text-green-800">Warranty Information</h4>
                  <p className="text-sm text-green-700 mt-1">
                    {viewDialog.service.warranty_days} days warranty included
                  </p>
                  {viewDialog.service.warranty_terms && (
                    <p className="text-sm text-green-600 mt-2">{viewDialog.service.warranty_terms}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, service: deleteDialog.service })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Service</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.service?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, service: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
          </div>
        </main>
      </div>
    </div>
  );
}
